import {LeagueResource} from "../resource/LeagueResource";
import {LeagueQuery} from "../resource/LeagueQuery";
import {League} from "../league/League";
import {LeagueFactory} from "../league/LeagueFactory";
import {UserLeagueInformation} from "../league/UserLeagueInformation";
import {
    IApiUserLeague, IApiLeagueRestriction, IApiContestant, IApiRanking,
    IApiMatchResult, IApiLeagueContestant
} from "../typings/apiResponse";
import {Contestant} from "../league/Contestant";
import {LeagueRestrictionFactory} from "../league/leagueRestriction/LeagueRestrictionFactory";
import {Ranking} from "../league/Ranking";
import {MatchResult} from "../league/MatchResult";
import {LeagueContestant} from "../league/LeagueContestant";
import * as angular from "angular";
import {find} from "lodash";
import {UserLeagueContestants} from "../league/UserLeagueContestants";
import {ContestantInfo} from "../league/ContestantInfo";

/**
 * @ngdoc service
 * @type {Object}
 * @name ngLeague.LeagueService
 *
 * @requires $log
 * @requires ngLeague.LeagueResource
 * @requires $q
 *
 * @description
 * The Leagueservice can be used to get a league object from the ESL Play API.
 *
 * See {@link ngLeague.League} and it's implementations: {@link ngLeague.Cup} for more information about the
 * results.
 */
export class LeagueService {

    public static $inject = ["LeagueResource", "$q"];
    constructor(
        private LeagueResource:LeagueResource,
        private $q: angular.IQService
    ) {

    }

    public getLeagues(query: LeagueQuery): angular.IPromise<Array<League>> {
        return this.LeagueResource.getLeagues(query).then(
            (leagues) => {
                const leagueObjects = [];

                leagues.forEach(
                    (league) => {
                        leagueObjects.push(LeagueFactory.getLeagueByType(league));
                    }
                );

                return leagueObjects;
            }
        );
    }

    public getLeague(id: number): angular.IPromise<League> {
        const query = new LeagueQuery().setId(id);

        return this.LeagueResource.getLeagues(query).then(
            (leagues) => {
                return LeagueFactory.getLeagueByType(leagues.pop());
            }
        );
    }

    public getLeagueRestrictions(leagueId: number, contestantId?: number): angular.IPromise<any[]> {
        return this.LeagueResource.getLeagueRestrictions(leagueId, contestantId).then((apiRestrictions: IApiLeagueRestriction[]) => {
            const restrictions = [];

            angular.forEach(apiRestrictions, (restriction: IApiLeagueRestriction) => {
                restrictions.push(LeagueRestrictionFactory.getRestrictionByType(restriction));
            });

            return restrictions;
        });
    }

    /**
     * Sign off of a league.
     * The API route called currently works for Cup only.
     */
    public signoff(leagueId: number, contestantId: number): angular.IPromise<void> {
        return this.LeagueResource.signoff(leagueId, contestantId).then(res => this.$q.resolve());
    }

    public signup(leagueId: number, contestantId: number): angular.IPromise<Contestant> {
        return this.LeagueResource.signUp(leagueId, contestantId).then(
            (apiContestant: IApiContestant) => {
                return new Contestant(apiContestant);
            }
        );
    }

    public checkin(leagueId: number, contestantId: number): angular.IPromise<Contestant> {
        return this.LeagueResource.checkIn(leagueId, contestantId)
            .then((apiContestant: IApiContestant) => new Contestant(apiContestant));
    }

    public getRanking(query: LeagueQuery): angular.IPromise<Ranking> {
        return this.LeagueResource.getRanking(query)
            .then((ranking: IApiRanking) => new Ranking(ranking));
    }

    public compareLeaguesByStartDate(leagueA: League, leagueB: League): number {
        return leagueA.getStartDate().getTime() - leagueB.getStartDate().getTime();
    }

    /**
     * @deprecated since user leagues will be removed soon.
     */
    public compareUserLeaguesByStartDate(userLeagueA: UserLeagueInformation, userLeagueB: UserLeagueInformation): number {
        return userLeagueA.league.getStartDate().getTime() - userLeagueB.league.getStartDate().getTime();
    }

    public getMatchResults(leagueId: number): angular.IPromise<MatchResult[]> {
        return this.LeagueResource.getMatchResults(leagueId).then((results: IApiMatchResult[]) => {
            return results.map(result => new MatchResult(result));
        });
    }

    public getLeagueContestants(leagueId: number): angular.IPromise<LeagueContestant[]> {
        return this.LeagueResource.getLeagueContestants(leagueId).then((contestants: IApiLeagueContestant[]) => {
            return contestants.map(contestant => new LeagueContestant(contestant));
        });
    }

    public getUserLeagueContestants(userId: number) {
        return this.LeagueResource.getUserLeagueContestants(userId).then(userLeagueContestantsList => {
            return userLeagueContestantsList.map(userLeagueContestants => new UserLeagueContestants(userLeagueContestants));
        });
    }

    public getUserContestantInfos(leagueId: number, userId: number) {
        return this.LeagueResource.getUserContestantInfos(leagueId, userId).then(contestantStates => {
            return contestantStates.map(state => new ContestantInfo(state));
        });
    }
}
